import React, { useMemo } from "react";
import { Box, Chip, Divider, Paper, Skeleton, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

const formatBytes = (n) => {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 10 ? 0 : 1)} ${units[i]}`;
};

const ptLabel = (pt, t) => {
  const map = {
    anonymization: t("adminBilling.pt.anonymization", "Anonymizácia"),
    pseudonymization: t("adminBilling.pt.pseudonymization", "Pseudonymizácia"),
    depseudonymization: t("adminBilling.pt.depseudonymization", "De-pseudonymizácia"),
  };
  return map[pt] || pt || "—";
};

export default function AdminBillingBreakdown({ data, loading }) {
  const { t } = useTranslation();

  const byProcessType = Array.isArray(data?.by_process_type) ? data.by_process_type : [];

  const statusChips = useMemo(() => {
    const byStatus = Array.isArray(data?.by_status) ? data.by_status : [];
    const m = new Map();
    byStatus.forEach((x) => m.set(x.status, x.count));
    return {
      succeeded: m.get("succeeded") || 0,
      failed: m.get("failed") || 0,
    };
  }, [data]);

  if (loading) {
    return (
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
          {t("adminBilling.breakdown.title", "Rozpis")}
        </Typography>
        <Skeleton variant="rectangular" height={180} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
        {t("adminBilling.breakdown.title", "Rozpis")}
      </Typography>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
          <Chip label={`${t("adminBilling.breakdown.succeeded", "OK")}: ${statusChips.succeeded}`} variant="outlined" />
          <Chip label={`${t("adminBilling.breakdown.failed", "Chyby")}: ${statusChips.failed}`} variant="outlined" />
        </Stack>

        <Divider sx={{ my: 2 }} />

        {byProcessType.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("adminBilling.breakdown.noData", "Žiadne dáta pre zvolené filtre.")}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {byProcessType.map((row) => (
              <Box
                key={row.process_type}
                sx={{
                  display: "flex",
                  gap: 2,
                  flexWrap: "wrap",
                  alignItems: "center",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 1,
                }}
              >
                <Chip size="small" label={ptLabel(row.process_type, t)} />

                <Typography variant="body2">
                  {t("adminBilling.breakdown.count", "Behy")}: <b>{row.count ?? 0}</b>
                </Typography>

                <Typography variant="body2">
                  {t("adminBilling.breakdown.pages", "Strany")}: <b>{row.pages ?? 0}</b>
                </Typography>

                <Typography variant="body2">
                  {t("adminBilling.breakdown.items", "Zásahy")}: <b>{row.anonymized ?? 0}</b>
                </Typography>

                <Typography variant="body2">
                  {t("adminBilling.breakdown.input", "Vstup")}: <b>{formatBytes(row.input_bytes ?? 0)}</b>
                </Typography>

                <Typography variant="body2">
                  {t("adminBilling.breakdown.output", "Výstup")}: <b>{formatBytes(row.output_bytes ?? 0)}</b>
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>
    </Box>
  );
}